import React, { useEffect, useState } from "react";
import { Box } from "theme-ui";
import { useNavigate } from "react-router-dom";
import { getActivePriceLocks } from "api/QuoteExpiration";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";

const fmtCurrency = (v: number) => "\u0024" + v.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtCurrencyFull = (v: number) => "\u0024" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const divider = "1px solid #F0F0F0";

const urgencyColors: Record<string, string> = {
  green: "#276749",
  orange: "#C05621",
  red: "#C53030",
  expired: "#C53030",
};

const urgencyBgColors: Record<string, string> = {
  green: "#C6F6D5",
  orange: "#FEEBC8",
  red: "#FED7D7",
  expired: "#FED7D7",
};

const ActivePriceLocksWidget: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getActivePriceLocks();
      setData(result);
      setLoading(false);
    };
    fetch();
  }, []);

  if (loading) return null;
  if (!data || data.summary.totalLocks === 0) return null;

  const { summary, locks } = data;

  return (
    <Box sx={{ marginBottom: "24px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, fontFamily: "Poppins, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: themevals.colors.black }}>Active Price Locks</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "#888", background: "#F2F2F2", padding: "0 6px", borderRadius: 8, lineHeight: "18px" }}>{summary.totalLocks}</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#2B6CB0" }}>{fmtCurrency(summary.totalLockedValue)} locked</span>
      </div>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 8, marginBottom: 10, fontFamily: "Poppins, sans-serif" }}>
        <div style={{ flex: 1, background: "#EBF8FF", borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#2B6CB0" }}>{summary.secured}</div>
          <div style={{ fontSize: 10, fontWeight: 500, color: "#2A4365" }}>Secured</div>
        </div>
        <div style={{ flex: 1, background: "#E6FFFA", borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#285E61" }}>{summary.pendingDeposit}</div>
          <div style={{ fontSize: 10, fontWeight: 500, color: "#234E52" }}>Pending Deposit</div>
        </div>
        <div style={{ flex: 1, background: "#F7FAFC", borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: themevals.colors.black }}>{fmtCurrencyFull(summary.totalDepositValue)}</div>
          <div style={{ fontSize: 10, fontWeight: 500, color: "#888" }}>Deposit Value</div>
        </div>
      </div>

      {/* Lock list */}
      <div style={{ border: divider, borderRadius: 6, overflow: "hidden", fontFamily: "Poppins, sans-serif" }}>
        {locks.map((lock: any, i: number) => {
          const isSecured = lock.depositStatus === "received";
          const pipColor = isSecured ? "#3182CE" : "#38B2AC";
          const statusLabel = isSecured ? "Secured" : "Pending";
          const statusColor = isSecured ? "#2B6CB0" : "#C05621";
          const statusBg = isSecured ? "#EBF8FF" : "#FEFCBF";

          return (
            <div key={lock.quoteId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: i < locks.length - 1 ? divider : "none" }}>
              {/* Urgency pip */}
              <span style={{ width: 3, alignSelf: "stretch", borderRadius: 2, backgroundColor: pipColor, flexShrink: 0 }} />

              {/* Lock info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: themevals.colors.black, lineHeight: "18px", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span>{lock.clientName || "Unknown"}</span>
                  <span style={{ fontWeight: 600 }}>{fmtCurrency(lock.quoteTotal)}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: statusColor, background: statusBg, padding: "0 5px", borderRadius: 4, lineHeight: "16px" }}>{statusLabel}</span>
                </div>
                <div style={{ fontSize: 11, color: "#888", lineHeight: "16px" }}>
                  {"GMQ-" + String(lock.sequence).padStart(5, "0")}
                  {lock.projectName && lock.projectName !== "Untitled" ? " \u00B7 " + lock.projectName : ""}
                  {lock.daysRemaining !== null && (
                    <>
                      {" \u00B7 "}
                      <span style={{ color: urgencyColors[lock.urgency] || "#888", fontWeight: lock.urgency === "red" || lock.urgency === "expired" ? 600 : 400 }}>
                        {lock.daysRemaining > 0
                          ? lock.daysRemaining + "d " + (isSecured ? "remaining" : "to pay")
                          : "Overdue"}
                      </span>
                    </>
                  )}
                  {lock.depositAmount > 0 && !isSecured && (
                    <>{" \u00B7 Dep: " + fmtCurrencyFull(lock.depositAmount)}</>
                  )}
                </div>
              </div>

              {/* View button */}
              <button onClick={() => navigate("/quotes/" + lock.quoteId)} style={{
                fontFamily: "Poppins", fontSize: 11, fontWeight: 500, cursor: "pointer", flexShrink: 0,
                border: "1px solid #D0D0D0", borderRadius: 4, padding: "3px 10px", lineHeight: "18px",
                color: themevals.colors.black, backgroundColor: "transparent",
              }}>View</button>
            </div>
          );
        })}
      </div>
    </Box>
  );
};

export default ActivePriceLocksWidget;
